/*
  Selleasy "Add-ons List" with an add-to-cart button per item (winner of A/B test
  BOS_2026Q3_09, Variant B "no drawer" — shipped to everyone).

  The Add-ons List (widget#pal) is rendered with the card that Selleasy's own cart-page add-on
  list (widget#cpal) uses: each item gets its own button that adds it straight away through
  Selleasy (so the `_lb-product` line property and the offer's discount code are applied as
  usual). An add from this widget only refreshes the cart (drawer contents + header count)
  without opening the drawer; adds from other Selleasy widgets still open it. Styling is
  Selleasy's own.

  QA on the live domain only: Selleasy applies discount codes through the Storefront API cart
  mutation, which returns 404 on *.shopifypreview.com, so discounts never apply on a preview.

  How: Selleasy builds each widget's template from its type and deep-merges
  `lbSetup.widgets[].diy.template` over it on every render. We hook the `window.lbSetup`
  assignment (made before the first widget render) to add the card override.
*/
(() => {
  const WIDGET_TYPE = 'widget#pal';
  const CARD_OVERRIDE = { style: 'lb-flat-cta-button', actionOnClick: true, button: 'lb-button-sm' };
  // Shown on the button for ~2s after an add. Selleasy's cart-page list uses "Added"; the
  // Add-ons List has none (it never adds by itself), which left the button blank.
  const AFTER_ACTION_TEXT = 'Added ✓';

  const patchSetup = (value) => {
    const widget = value && Array.isArray(value.widgets)
      && value.widgets.find((w) => w.recordType === WIDGET_TYPE);
    if (!widget) return null;

    widget.diy = widget.diy || {};
    const template = widget.diy.template || {};
    template.card = Object.assign({}, template.card, CARD_OVERRIDE);
    widget.diy.template = template;

    // Read by Selleasy after each add: 'stay' = call drawerRefresh() (no redirect), which we
    // turn into a refresh-only below. The Add-ons List has no afterAction of its own since it
    // normally never adds by itself.
    widget.configs = widget.configs || {};
    widget.configs.afterAction = 'stay';

    widget.contents = widget.contents || {};
    widget.contents.afterActionText = AFTER_ACTION_TEXT;
    return widget;
  };

  // Widgets rendered before this script ran: point them at the patched `diy` and re-render.
  // Their `contents` is a copy made at render time, so the button text is set on it too.
  const rerenderWidgets = (widget) => {
    document.querySelectorAll(`lb-upsell-widget[widget="${WIDGET_TYPE}"]`).forEach((el) => {
      if (!el.lbPayload || !el.lbPayload.widget) return;
      el.lbPayload.widget.diy = widget.diy;
      el.lbPayload.widget.contents = Object.assign({}, el.lbPayload.widget.contents, { afterActionText: AFTER_ACTION_TEXT });
      el.lbPayload = Object.assign({}, el.lbPayload);
    });
  };

  let setup = window.lbSetup;
  const widget = patchSetup(setup);
  if (widget) rerenderWidgets(widget);

  Object.defineProperty(window, 'lbSetup', {
    configurable: true,
    get: () => setup,
    set: (value) => {
      setup = value;
      patchSetup(value);
    }
  });

  // After an add, Selleasy calls lbSelleasyUtils.drawerRefresh(), which refreshes AND opens the
  // drawer. When the add came from this widget, swap that for the theme's refresh-only event
  // (re-renders the drawer contents and the header cart count).
  let suppressOpenUntil = 0;
  window.addEventListener('lbWidgetCta', (evt) => {
    if (evt.detail.type !== WIDGET_TYPE) return;

    const utils = window.lbSelleasyUtils;
    if (utils && utils.drawerRefresh && !utils.drawerRefresh.rvWrapped) {
      const originalRefresh = utils.drawerRefresh;
      utils.drawerRefresh = function (...args) {
        if (Date.now() < suppressOpenUntil) {
          suppressOpenUntil = 0;
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          return Promise.resolve();
        }
        return originalRefresh.apply(this, args);
      };
      utils.drawerRefresh.rvWrapped = true;
    }
    // Time-boxed so a failed add can't swallow a later drawer open from another widget.
    suppressOpenUntil = Date.now() + 15000;
  });
})();
